import { Link } from 'react-router-dom';
import { BookOpen, ClipboardCheck, ScanLine, Wrench, ShieldCheck } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/shared/auth/AuthProvider';

interface Tutorial {
  id: string;
  title: string;
  roleScope: string[];
  steps: string[];
  actionTo: string;
  actionLabel: string;
}

const TUTORIALS: Tutorial[] = [
  {
    id: 'cadastro-itens',
    title: 'Cadastro de itens e modelos',
    roleScope: ['ADMIN', 'SUPERVISOR'],
    steps: [
      'Acesse Administração > Modelos de Item e cadastre SKU, nome e compatibilidade térmica.',
      'Acesse Administração > Inventário para cadastrar cada instância física com barcode.',
      'Opcionalmente envie imagem do modelo para facilitar conferência visual.',
      'Valide o item com leitura de teste em uma estação operacional.',
    ],
    actionTo: '/item-models',
    actionLabel: 'Abrir Modelos de Item',
  },
  {
    id: 'verificacao-kit-sujo',
    title: 'Verificação rápida na sala suja',
    roleScope: ['ADMIN', 'SUPERVISOR', 'NURSE_RT', 'NURSE', 'TECHNICIAN'],
    steps: [
      'Acesse Sala Suja no menu principal.',
      'Use os botões grandes para abrir Expurgo e validar kit/equipamento por leitura.',
      'Se houver divergência, mova para quarentena com justificativa.',
      'Registre observações críticas antes de seguir para lavagem.',
    ],
    actionTo: '/sala-suja',
    actionLabel: 'Abrir Sala Suja',
  },
  {
    id: 'liberacao-lotes',
    title: 'Liberação de lotes por perfil autorizado',
    roleScope: ['ADMIN', 'SUPERVISOR', 'NURSE_RT'],
    steps: [
      'Abra Operacional > Liberação e selecione o lote.',
      'Confira parâmetros do ciclo e indicadores químicos/biológicos.',
      'Aprove o lote apenas se todos os critérios estiverem em conformidade.',
      'Em caso de não conformidade, retenha para reprocesso e acione recall quando aplicável.',
    ],
    actionTo: '/liberacao',
    actionLabel: 'Abrir Liberação',
  },
  {
    id: 'impressao-etiquetas',
    title: 'Impressão com preview visual',
    roleScope: ['ADMIN', 'SUPERVISOR', 'NURSE_RT', 'NURSE', 'TECHNICIAN'],
    steps: [
      'Finalize o fluxo operacional (ex.: preparo de kit).',
      'Revise a pré-visualização com QR, barcode e dados da etiqueta.',
      'Escolha o modo de impressão (Zebra ou impressora comum).',
      'Imprima e valide leitura com scanner no item gerado.',
    ],
    actionTo: '/configuracoes',
    actionLabel: 'Configurar Impressão',
  },
];

export function AjudaPage() {
  const { user } = useAuth();
  const role = user?.role ?? 'VIEWER';
  const visible = TUTORIALS.filter((t) => t.roleScope.includes(role));

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">Central de ajuda</h1>
        <p className="text-sm text-neutral-500">
          Procedimentos rápidos por perfil para reduzir erros de operação e acelerar o treinamento.
        </p>
      </header>

      <Card>
        <CardHeader
          title="Perfil ativo"
          subtitle="Conteúdo adaptado ao seu nível de acesso"
          action={<Badge tone="brand">{role}</Badge>}
        />
        <CardBody>
          <div className="grid gap-2 text-sm text-neutral-700 dark:text-neutral-300 sm:grid-cols-2">
            <div className="rounded border border-neutral-200 p-3 dark:border-neutral-700">
              <ShieldCheck className="mb-1 h-4 w-4 text-brand-600" />
              Apenas tutoriais permitidos para o seu papel aparecem abaixo.
            </div>
            <div className="rounded border border-neutral-200 p-3 dark:border-neutral-700">
              <BookOpen className="mb-1 h-4 w-4 text-brand-600" />
              Para material completo, consulte também o site de documentação técnica e funcional.
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {visible.map((tutorial) => (
          <Card key={tutorial.id}>
            <CardHeader
              title={tutorial.title}
              action={
                <Badge tone="neutral">
                  {tutorial.id === 'cadastro-itens' ? <ClipboardCheck className="h-3.5 w-3.5" /> : null}
                  {tutorial.id === 'verificacao-kit-sujo' ? <ScanLine className="h-3.5 w-3.5" /> : null}
                  {tutorial.id === 'liberacao-lotes' ? <ShieldCheck className="h-3.5 w-3.5" /> : null}
                  {tutorial.id === 'impressao-etiquetas' ? <Wrench className="h-3.5 w-3.5" /> : null}
                </Badge>
              }
            />
            <CardBody className="space-y-3">
              <ol className="ml-4 list-decimal space-y-1 text-sm text-neutral-700 dark:text-neutral-300">
                {tutorial.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              <Link
                to={tutorial.actionTo}
                className="ict-focus-ring inline-flex items-center rounded border border-brand-300 px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-300 dark:hover:bg-brand-900/30"
              >
                {tutorial.actionLabel}
              </Link>
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
}
